import useUtilStore from "@/data/util";
import { StoreEnum } from "@/enums";
import { IMaybe, ISelect } from "@/types";
import { create, StateCreator } from "zustand";
import { devtools } from "zustand/middleware";
import useHFStore from "./HFLevel";
import useLGAStore from "./LgaLevel";
import useRevitalizationStore from "./Revitalization";
import useStateStore from "./StateLevel";
import useWardStore from "./WardLevel";

export interface ZoneState {
  selectedZone: IMaybe<string>;
  zoneList: IMaybe<ISelect[]>;
  zoneName: IMaybe<string>;
}

export interface ZoneActions {
  setSelectedZone: (zone: IMaybe<string>) => void;
  setZoneList: (zoneList: IMaybe<ISelect[]>) => void;
  onZoneChange: (id: IMaybe<string>, stateId?: IMaybe<string>) => void;
  resetSelections: () => void;
}

export const storeCreator: StateCreator<ZoneState & ZoneActions> = (set) => ({
  selectedZone: null,
  zoneList: null,
  zoneName: null,
  setSelectedZone: (payload) => {
    const { zoneList } = useZoneStore.getState();
    if (typeof payload !== "string" && payload !== null) {
      throw new Error("Invalid payload type");
    }
    set({
      selectedZone: payload,
      zoneName:
        (zoneList?.find((el) => el?.value === payload)?.label as string) ??
        null,
    });
  },

  // Set the zone list
  setZoneList: (zoneList) => set({ zoneList }),

  // Handle zone change
  onZoneChange: async (id = null, stateId = null) => {
    const { setSearchPhcText } = useUtilStore.getState();
    const { onStateChange, stateList } = useStateStore.getState();
    const { resetSelections, setSelectedZone } = useZoneStore.getState();
    const { setRvQuery } = useRevitalizationStore.getState();

    resetSelections();

    setSearchPhcText("");

    if (!id) return onStateChange(null);

    setSelectedZone(id);
    if (stateId && stateList && stateList?.length > 0) onStateChange(stateId);
    setRvQuery(`?zone_id=${id}`);
  },
  resetSelections: () => {
    const { setSelectedState } = useStateStore.getState();
    const { setSelectedLga } = useLGAStore.getState();
    const { setSelectedWard } = useWardStore.getState();
    const { setSelectedHF } = useHFStore.getState();
    const { setStateList } = useStateStore.getState();

    setSelectedState(null);
    setSelectedLga(null);
    setSelectedWard(null);
    setSelectedHF(null);
    setStateList([]);
  },
});

const useZoneStore = create<ZoneState & ZoneActions>(
  devtools(storeCreator, { name: StoreEnum.ZONE_STORE }) as StateCreator<
    ZoneState & ZoneActions
  >
);

export default useZoneStore;
